import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { useData } from '../../context/DataContext';
import apiClient from '../../services/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import hospitalImage from '../../assets/homepageimages/hospital-image.webp';
import PageHero from './PageHero';

// WhatsApp Icon Component
const WhatsAppIcon = ({ sx }) => (
  <Box sx={{ ...sx, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ width: '100%', height: '100%' }}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  </Box>
);

const Contact = () => {
  const { state } = useData();
  const { pages, hospitalInfo } = state;
  const contactInfo = pages.contact;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear errors when user starts typing
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitError('Please fill in all required fields.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      const result = await apiClient.submitContactForm(formData);
      
      if (result && result.success) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError(result?.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError(error.message || 'Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHero
        src={hospitalImage}
        alt="Contact Us"
        title="Contact Us"
        subtitle="Get in touch with us for appointments, inquiries, or emergency services"
        shellSx={{
          height: { xs: '35vh', sm: '40vh', md: '45vh' },
          minHeight: { xs: 200, sm: 250, md: 300 },
        }}
      />

      {/* Contact Information */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" gutterBottom>
              Send us a Message
            </Typography>
            <Typography variant="body2" gutterBottom>Feedback and suggestions</Typography>
            <Card sx={{ p: 3 }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {submitSuccess && (
                    <Grid item xs={12}>
                      <Alert severity="success" onClose={() => setSubmitSuccess(false)}>
                        Thank you for your message! We will get back to you soon.
                      </Alert>
                    </Grid>
                  )}
                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error" onClose={() => setSubmitError('')}>
                        {submitError}
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" gutterBottom>
              Contact Information
            </Typography>
            <Grid container spacing={3}>
              {hospitalInfo.showAddress && (
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <LocationOnIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {hospitalInfo.address}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )}
              {hospitalInfo.showContact && (
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <PhoneIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Phone
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {hospitalInfo.contact}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )}
              {hospitalInfo.showEmail && (
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <EmailIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Email
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {hospitalInfo.email}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Card 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => {
                    const phoneNumber = '03315405999';
                    window.open(`https://wa.me/${phoneNumber}`, '_blank');
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      <WhatsAppIcon />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      WhatsApp
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      For Feedback / Complaints
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      03315405999
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Map */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ width: '100%', height: 500, overflow: 'hidden', borderRadius: 2 }}>
            {hospitalInfo.showMapLink ? (
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3329.857964479836!2d73.0555533153526!3d33.5898869496247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38df951b2e2c7e2d%3A0x6e2e2e2e2e2e2e2e!2sHearts%20International%20Hospital!5e0!3m2!1sen!2s!4v1717171717171"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Hearts International Hospital Location"
              />
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: '#f5f5f5'
              }}>
                <Typography variant="h6" color="text.secondary">
                  Map is currently hidden
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact; 